import { useEffect, useState } from 'react';
import { X, Undo2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useUndoStore, UndoAction } from '@/hooks/useUndoManager';

/**
 * Global undo toast that appears in bottom-left corner
 * Consumes the undo store and displays active undo actions
 */
export function GlobalUndoToast() {
  const { actions, executeUndo, removeAction } = useUndoStore();
  const [activeAction, setActiveAction] = useState<UndoAction | null>(null);
  const [progress, setProgress] = useState(100);

  // Get the most recent action
  useEffect(() => {
    if (actions.length > 0) {
      const latest = actions[actions.length - 1];
      setActiveAction(latest);
      setProgress(100);
    } else {
      setActiveAction(null);
    }
  }, [actions]);

  // Progress bar countdown
  useEffect(() => {
    if (!activeAction) {
      return;
    }

    const duration = activeAction.timeout || 8000;
    const startTime = Date.now();

    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);

      if (remaining === 0) {
        clearInterval(interval);
        removeAction(activeAction.id);
      }
    }, 50);

    return () => clearInterval(interval);
  }, [activeAction, removeAction]);

  const handleUndo = async () => {
    if (activeAction) {
      await executeUndo(activeAction.id);
    }
  };

  const handleClose = () => {
    if (activeAction) {
      removeAction(activeAction.id);
    }
  };

  return (
    <AnimatePresence>
      {activeAction && (
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 50, scale: 0.9 }}
          className="fixed bottom-6 left-6 z-[9998] max-w-md"
          role="alert"
          aria-live="polite"
        >
          <div className="relative overflow-hidden rounded-xl border border-app-border bg-app-surface shadow-2xl">
            {/* Progress bar */}
            <div className="absolute bottom-0 left-0 h-1 bg-app-primary/20 w-full">
              <motion.div
                className="h-full bg-app-primary"
                style={{ width: `${progress}%` }}
                transition={{ duration: 0.05, ease: 'linear' }}
              />
            </div>

            <div className="flex items-center gap-4 p-4 pr-12">
              <div className="flex-1">
                <p className="text-sm font-medium text-app-text">
                  {activeAction.message}
                </p>
              </div>

              <button
                onClick={handleUndo}
                className="inline-flex items-center gap-2 rounded-lg bg-app-primary px-4 py-2 text-sm font-semibold text-white hover:bg-app-primary/90 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-app-primary focus-visible:ring-offset-2"
                aria-label="Fortryd handling"
              >
                <Undo2 className="h-4 w-4" />
                Fortryd
              </button>

              <button
                onClick={handleClose}
                className="absolute right-2 top-2 rounded-lg p-2 hover:bg-app-secondary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-app-primary"
                aria-label="Luk"
              >
                <X className="h-4 w-4 text-app-text-secondary" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}












